import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import Navigation from "@/components/Navigation";
import { 
  BookOpen, 
  Award, 
  CheckCircle2, 
  Clock, 
  Trophy,
  ShieldCheck,
  Users,
  Zap,
  Lightbulb,
  MessageCircle,
  Target,
  LogOut
} from "lucide-react";

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certificateEligible, setCertificateEligible] = useState(false);

  const moduleIcons = {
    1: Target,
    2: Zap,
    3: ShieldCheck,
    4: Lightbulb,
    5: MessageCircle,
    6: CheckCircle2,
    7: Users
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modulesRes, progressRes, certRes] = await Promise.all([
        axios.get(`${API}/modules`),
        axios.get(`${API}/progress`),
        axios.get(`${API}/certificate/check`)
      ]);
      setModules(modulesRes.data);
      setProgress(progressRes.data);
      setCertificateEligible(certRes.data.eligible);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getModuleProgress = (moduleId) => {
    return progress.find(p => p.module_id === moduleId);
  };

  const calculateOverallProgress = () => {
    if (modules.length === 0) return 0;
    const completed = progress.filter(p => p.completed).length;
    return Math.round((completed / modules.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-cream-50">
      <Navigation user={user} onLogout={onLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Progress Overview */}
        <div className="mb-8 animate-fadeIn">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl">Votre progression</CardTitle>
                  <CardDescription>Continuez à apprendre pour obtenir votre certificat</CardDescription>
                </div>
                {certificateEligible && (
                  <Button
                    onClick={() => navigate("/certificate")}
                    className="btn-secondary text-white"
                    data-testid="view-certificate-button"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Voir mon certificat
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {progress.filter(p => p.completed).length} / {modules.length} modules complétés
                  </span>
                  <span className="font-semibold text-orange-600">{calculateOverallProgress()}%</span>
                </div>
                <Progress value={calculateOverallProgress()} className="h-3" data-testid="overall-progress-bar" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modules Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {modules.map((module, index) => {
            const moduleProgress = getModuleProgress(module.id);
            const IconComponent = moduleIcons[module.order] || BookOpen;
            const isCompleted = moduleProgress?.completed || false;
            const bestScore = moduleProgress?.best_score || 0;

            return (
              <Card
                key={module.id}
                className="card-hover cursor-pointer border-0 shadow-lg animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/module/${module.id}`)}
                data-testid={`module-card-${module.order}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="p-3 bg-gradient-to-br from-orange-100 to-purple-100 rounded-xl">
                      <IconComponent className="w-6 h-6 text-orange-600" />
                    </div>
                    {isCompleted && (
                      <Badge className="bg-green-500 text-white" data-testid={`module-${module.order}-completed-badge`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Complété
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg mt-4">
                    Module {module.order}: {module.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {module.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {moduleProgress && moduleProgress.quiz_attempts.length > 0 ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Meilleur score:</span>
                        <span className={`font-semibold ${
                          bestScore >= 80 ? "text-green-600" : "text-orange-600"
                        }`} data-testid={`module-${module.order}-score`}>
                          {bestScore.toFixed(0)}%
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-2" />
                        Pas encore commencé
                      </div>
                    )}
                    <Button
                      className="w-full btn-primary text-white"
                      data-testid={`start-module-${module.order}-button`}
                    >
                      {isCompleted ? "Revoir" : "Commencer"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Certificate CTA */}
        {!certificateEligible && modules.length > 0 && (
          <Card className="mt-8 border-2 border-dashed border-orange-300 bg-orange-50/50 animate-fadeIn">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Trophy className="w-8 h-8 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Obtenez votre certificat d'animateur parascolaire
                  </h3>
                  <p className="text-sm text-gray-600">
                    Complétez tous les modules avec un score minimum de 80% pour recevoir votre certificat numérique.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
